"use client";

import { consumeSubscribeSuccessOrigin } from "@/app/(homepage)/subscribe-success/subscribeSuccessHandoff";
import Button from "@/components/design-system/button";
import Modal from "@/components/design-system/modal";
import SuccessCheckIcon from "public/next-public/common/icons/toast-success-check-icon.svg";
import { useCallback, useEffect, useState } from "react";
import styles from "./subscribeSuccessDialog.module.scss";

const AUTO_CLOSE_SECONDS = 5;

/**
 * Confirms a Weekly Specials signup that happened on another page. Pages that subscribe away from
 * the homepage record a handoff and navigate here; this reads it once and shows the confirmation.
 */
export default function SubscribeSuccessDialog() {
  const [show, setShow] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(AUTO_CLOSE_SECONDS);

  const hide = useCallback(() => setShow(false), []);

  useEffect(() => {
    const origin = consumeSubscribeSuccessOrigin();
    if (!origin) {
      return;
    }

    // Second gate: only pop the dialog when the browser agrees we arrived from that page, so a
    // handoff left behind by an abandoned tab can't trigger it.
    const referrerPath = (() => {
      try {
        return document.referrer ? new URL(document.referrer).pathname : "";
      } catch {
        return "";
      }
    })();
    if (!referrerPath.startsWith(origin)) {
      return;
    }

    setShow(true);
  }, []);

  useEffect(() => {
    if (!show) {
      return;
    }
    if (secondsLeft <= 0) {
      setShow(false);
      return;
    }
    const timer = window.setTimeout(() => setSecondsLeft((current) => current - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [show, secondsLeft]);

  if (!show) {
    return null;
  }

  return (
    <Modal show={show} onHide={hide} size="skinny" ariaLabel="Thanks for subscribing" bodyClassName={styles.body}>
      <p className={styles.countdown} aria-hidden>
        Will close in {secondsLeft}
      </p>
      <SuccessCheckIcon width={48} height={48} aria-hidden className={styles.icon} />
      <h2 className={styles.title}>Thanks for subscribing!</h2>
      <p className={styles.message}>You&apos;ll now start receiving email updates.</p>
      <Button
        variant="primary"
        color="blue"
        size="large"
        text="Got it!"
        showLeftIcon={false}
        showRightIcon={false}
        onClick={hide}
        className={styles.confirmButton}
      />
    </Modal>
  );
}
